import { useMemo, useState } from 'react';
import { useGlobal } from 'reactn';

import { ForumIcon, GroupIcon, NotificationsIcon } from '@orijinworks/frontend-commons';
import { checkUserRoleType } from './mappers/feedUtility';
import { ROLES } from '../../../constants';
import { isPermitted } from '../../../components/permission-control/permission-control';
import { FEED_VIEW, FEED_ANNOUNCEMENT } from './FeedPermission';

export const useFeedConfigureHook = (props) => {
  const {
    unreadCount,
    unreadAnnouncementsCount,
    unreadMessagesCount,
    resetAnnouncementAttachments
  } = props;

  const [global] = useGlobal();
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [modalContext, setModalContext] = useState('');
  const [editSettings, setEditSettings] = useState(null);


  // Event handlers
  const handleNewMessage = (context) => {
    setShowNewMessageModal(true);
    setModalContext(context);
  };

  const handleCloseModal = () => {
    setShowNewMessageModal(false);
    resetAnnouncementAttachments?.();
  };

  const handleOpenEditModal = (settings) => {
    setEditSettings(settings || null);
    setShowNewMessageModal(true);
    setModalContext('edit');
  };

  // Computed values
  const allCount = unreadCount || 0;

  // Check if user is attorney
  const isAttorney = useMemo(() => {
    return checkUserRoleType([ROLES.ATTORNEY]);
  }, []);

  // Tab visibility by permission: Messages -> Feed_View, Announcements -> Feed_Announcement_View_All
  const canViewMessagesTab = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FEED_VIEW, permissionIds);
  }, [global.cbacConfigFlag, global.permissions, global.permissionIds]);

  const canViewAnnouncementsTab = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FEED_ANNOUNCEMENT, permissionIds);
  }, [global.cbacConfigFlag, global.permissions, global.permissionIds]);

  // Tab configuration data
  const tabsConfig = useMemo(() => {
    return [
      {
        key: 'all',
        label: 'All',
        value: 'All',
        Icon: ForumIcon,
        hasUnreadCount: (allCount ?? 0) > 0,
        unreadCount: allCount,
        hide: true,
      },
      {
        key: 'messages',
        label: 'Messages',
        value: 'Messages',
        Icon: GroupIcon,
        hasUnreadCount: (unreadMessagesCount ?? 0) > 0,
        unreadCount: unreadMessagesCount || 0,
        hide: !canViewMessagesTab, // Hide Messages tab when user lacks Feed_View
      },
      {
        key: 'announcements',
        label: 'Announcements',
        value: 'Announcements',
        Icon: NotificationsIcon,
        hasUnreadCount: (unreadAnnouncementsCount ?? 0) > 0,
        unreadCount: unreadAnnouncementsCount ?? 0,
        hide: isAttorney || !canViewAnnouncementsTab, // Hide when attorney or lacks FEED_ANNOUNCEMENT
      },
    ]
  }, [allCount, unreadAnnouncementsCount, unreadMessagesCount, isAttorney, canViewMessagesTab, canViewAnnouncementsTab]);

  const onTabChangeHandler = (event, value) => {
    props.onTabChangeHandler && props.onTabChangeHandler(event, value);
  };
  return {
    // State
    showNewMessageModal,
    modalContext,
    editSettings,

    // Computed values
    tabsConfig,
    allCount,

    // Event handlers
    handleNewMessage,
    handleCloseModal,
    handleOpenEditModal,
    onTabChangeHandler,
  };
};